import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { ToastProvider } from './components/ui/Toast';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppShell } from './components/AppShell';
import { PageLoader } from './components/ui/Spinner';
import { LandingPage } from './pages/LandingPage';

// Retry dynamic imports — on chunk 404 after deploy, reload to get fresh index.html
function lazyWithRetry<T extends React.ComponentType<any>>(importFn: () => Promise<{ default: T }>) {
  return lazy(() =>
    importFn().catch(() => {
      window.location.reload();
      return new Promise<{ default: T }>(() => {}); // never resolves, page reloads
    })
  );
}

// Lazy load page components for code splitting
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazyWithRetry(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const LoansPage = lazyWithRetry(() => import('./pages/LoansPage').then(m => ({ default: m.LoansPage })));
const LoanDetailPage = lazyWithRetry(() => import('./pages/LoanDetailPage').then(m => ({ default: m.LoanDetailPage })));
const InvestorsPage = lazyWithRetry(() => import('./pages/InvestorsPage').then(m => ({ default: m.InvestorsPage })));
const InvestorDetailPage = lazyWithRetry(() => import('./pages/InvestorDetailPage').then(m => ({ default: m.InvestorDetailPage })));
const ReportingPage = lazyWithRetry(() => import('./pages/ReportingPage').then(m => ({ default: m.ReportingPage })));
const IntakeFormsPage = lazyWithRetry(() => import('./pages/IntakeFormsPage').then(m => ({ default: m.IntakeFormsPage })));
const IntakeFormEditorPage = lazyWithRetry(() => import('./pages/IntakeFormEditorPage').then(m => ({ default: m.IntakeFormEditorPage })));
const NotificationsPage = lazyWithRetry(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const PublicIntakeFormPage = lazyWithRetry(() => import('./pages/PublicIntakeFormPage').then(m => ({ default: m.PublicIntakeFormPage })));
const ComponentDemo = lazyWithRetry(() => import('./pages/ComponentDemo').then(m => ({ default: m.ComponentDemo })));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const W9UploadPage = lazyWithRetry(() => import('./pages/W9UploadPage').then(m => ({ default: m.W9UploadPage })));
const AdminPage = lazyWithRetry(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const AffiliatesPage = lazyWithRetry(() => import('./pages/AffiliatesPage').then(m => ({ default: m.AffiliatesPage })));
const ReferralsPage = lazyWithRetry(() => import('./pages/ReferralsPage').then(m => ({ default: m.ReferralsPage })));
const InvestorPortalPage = lazyWithRetry(() => import('./pages/InvestorPortalPage').then(m => ({ default: m.InvestorPortalPage })));
const PublicPayoffToolPage = lazyWithRetry(() => import('./pages/PublicPayoffToolPage'));
const PromoRedirectPage = lazyWithRetry(() => import('./pages/PromoRedirectPage').then(m => ({ default: m.PromoRedirectPage })));

// Loading fallback for Suspense
function RouteLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <PageLoader label="Loading..." />
    </div>
  );
}

// Loading fallback for protected routes (inside AppShell)
function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full min-h-[400px]">
      <PageLoader label="Loading..." />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
      <ToastProvider>
        <ConfirmProvider>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/apply/:slug" element={<PublicIntakeFormPage />} />
              <Route path="/w9/:token" element={<W9UploadPage />} />
              <Route path="/investor-portal/:token" element={<InvestorPortalPage />} />
              <Route path="/payoff-calculator" element={<PublicPayoffToolPage />} />
              <Route path="/amy" element={<PromoRedirectPage code="AMY2026" campaign="amy" />} />
              <Route path="/rpm" element={<PromoRedirectPage code="RPM2026" campaign="rpm" />} />

              {/* Protected routes with AppShell */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <DashboardPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/loans"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <LoansPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/loans/:id"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <LoanDetailPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investors"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <InvestorsPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investors/:id"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <InvestorDetailPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reporting"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <ReportingPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              {/* Legacy routes redirect to reporting page */}
              <Route path="/investor-earnings" element={<Navigate to="/reporting?tab=investor-reports" replace />} />
              <Route path="/borrower-reports" element={<Navigate to="/reporting?tab=borrower-reports" replace />} />
              <Route
                path="/intake-forms"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <IntakeFormsPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/intake-forms/:id/edit"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <IntakeFormEditorPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <NotificationsPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />

              {/* Settings Page */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <SettingsPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />

              {/* Admin Dashboard */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <AdminPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />

              {/* Referrals (affiliate dashboard for lenders) */}
              <Route
                path="/referrals"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <ReferralsPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />

              {/* Admin Affiliates */}
              <Route
                path="/admin/affiliates"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <AffiliatesPage />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />

              {/* Component Demo (development only) */}
              <Route
                path="/demo"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Suspense fallback={<PageLoadingFallback />}>
                        <ComponentDemo />
                      </Suspense>
                    </AppShell>
                  </ProtectedRoute>
                }
              />

              {/* Landing page */}
              <Route path="/" element={<LandingPage />} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ConfirmProvider>
      </ToastProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
